import { getApiUrl, getApiHeaders, apiFetch } from '../../shared/utils/api';
import { getAccessToken } from '../../shared/auth/okta';
import type { Me, RoleOut, UserOut, PermissionsGroupedOut, RoutePermissionOut, PaginatedUsersOut } from './types';

const base = () => getApiUrl();
const headers = () => getApiHeaders(getAccessToken());

export async function getMe(): Promise<Me> {
  const res = await apiFetch(`${base()}/api/me`, { headers: headers() });
  if (!res.ok) throw new Error(res.status === 401 ? 'Unauthorized' : `Me: ${res.status}`);
  return res.json();
}

export async function getRoles(): Promise<RoleOut[]> {
  const res = await apiFetch(`${base()}/api/admin/roles`, { headers: headers() });
  if (!res.ok) throw new Error(`Roles: ${res.status}`);
  return res.json();
}

export async function getRole(roleId: number): Promise<RoleOut> {
  const res = await apiFetch(`${base()}/api/admin/roles/${roleId}`, { headers: headers() });
  if (!res.ok) throw new Error(`Role: ${res.status}`);
  return res.json();
}

export async function updateRole(roleId: number, body: { name?: string; description?: string }): Promise<RoleOut> {
  const res = await apiFetch(`${base()}/api/admin/roles/${roleId}`, {
    method: 'PATCH',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Update role: ${res.status}`);
  return res.json();
}

export async function setRolePermissions(
  roleId: number,
  body: { permission_ids?: number[]; permission_slugs?: string[] }
): Promise<RoleOut> {
  const res = await apiFetch(`${base()}/api/admin/roles/${roleId}/permissions`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Set role permissions: ${res.status}`);
  return res.json();
}

export async function getPermissionsGrouped(): Promise<PermissionsGroupedOut[]> {
  const res = await apiFetch(`${base()}/api/admin/permissions`, { headers: headers() });
  if (!res.ok) throw new Error(`Permissions: ${res.status}`);
  return res.json();
}

export async function getRoutePermissions(): Promise<RoutePermissionOut[]> {
  const res = await apiFetch(`${base()}/api/admin/route-permissions`, { headers: headers() });
  if (!res.ok) throw new Error(`Route permissions: ${res.status}`);
  return res.json();
}

export async function getUsers(params: { page?: number; page_size?: number; search?: string; role_id?: number }): Promise<PaginatedUsersOut> {
  const q = new URLSearchParams();
  if (params.page != null) q.set('page', String(params.page));
  if (params.page_size != null) q.set('page_size', String(params.page_size));
  if (params.role_id != null) q.set('role_id', String(params.role_id));
  if (params.search) q.set('search', params.search);
  const res = await apiFetch(`${base()}/api/admin/users?${q}`, { headers: headers() });
  if (!res.ok) throw new Error(`Users: ${res.status}`);
  return res.json();
}

export async function setUserRole(userId: string, roleId: number): Promise<UserOut> {
  const res = await apiFetch(`${base()}/api/admin/users/${encodeURIComponent(userId)}/role`, {
    method: 'PATCH',
    headers: headers(),
    body: JSON.stringify({ role_id: roleId }),
  });
  if (!res.ok) throw new Error(`Set user role: ${res.status}`);
  return res.json();
}

export async function setUserRoles(userId: string, roleIds: number[]): Promise<UserOut> {
  const res = await apiFetch(`${base()}/api/admin/users/${encodeURIComponent(userId)}/roles`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify({ role_ids: roleIds }),
  });
  if (!res.ok) throw new Error(`Set user roles: ${res.status}`);
  return res.json();
}

export async function getUserPermissions(userId: string): Promise<UserOut> {
  const res = await apiFetch(`${base()}/api/admin/users/${encodeURIComponent(userId)}/permissions`, {
    headers: headers(),
  });
  if (!res.ok) throw new Error(`User permissions: ${res.status}`);
  return res.json();
}

export async function setUserPermissions(
  userId: string,
  body: { permission_ids?: number[]; permission_slugs?: string[] }
): Promise<UserOut> {
  const res = await apiFetch(`${base()}/api/admin/users/${encodeURIComponent(userId)}/permissions`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Set user permissions: ${res.status}`);
  return res.json();
}
